/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import Helmet from '@/components/meta/Helmet';
import NewsTemplate from '@/components/templates/News';
import { AUTHOR_PARAM_KEY } from '@/config/constants/query';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import { dayjst } from '@/lib/dayjst';
import { client } from '@/lib/microcms';
import { Author } from '@/lib/microcms/client/types/model';
import { getNewsTypeName } from '@/lib/news';
import { NewsRoutesProps } from '@/routes/News';

import { Content, NewsType } from './types';

const LIMIT = 10; // 一度に取得する記事件数;

function initData<T>(init: T): { [key in NewsType]: T } {
  return {
    [NewsType.News]: init,
    [NewsType.Campaign]: init,
    [NewsType.Blog]: init,
  };
}

const Fields: { [key in NewsType]: string } = {
  [NewsType.News]: 'id,title,publishedAt,updatedAt',
  [NewsType.Campaign]: 'id,featuredImage,title,publishedAt,updatedAt',
  [NewsType.Blog]: 'id,featuredImage,title,publishedAt,updatedAt',
};

const News: FC<NewsRoutesProps> = ({ newsType }) => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const [contents, setContents] = useState<{ [key in NewsType]: Content[] }>(
    initData([]),
  );
  const [authors, setAuthors] = useState<Author[]>([]);
  const [selectedAuthorId, setSelectedAuthorId] = useState<string>(
    queryParams.get(AUTHOR_PARAM_KEY) || '',
  );
  const [hasNextPage, setHasNextPage] = useState<{
    [key in NewsType]: boolean;
  }>(initData(true));
  const [offset, setOffset] = useState<{ [key in NewsType]: number }>(
    initData(0),
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const fetchContents = async (initialize: boolean = false) => {
    setIsFetching(true);

    let _offset = offset[newsType];
    let _contents = contents[newsType];

    if (initialize) {
      _offset = 0;
      _contents = [];
      setHasNextPage(initData(true));
      setOffset(initData(0));
    }

    const getFilters = () => {
      if (newsType === NewsType.Blog && selectedAuthorId) {
        return `author[equals]${selectedAuthorId}`;
      }
      if (newsType === NewsType.Campaign) {
        return `nonPublic[equals]false`;
      }
      return '';
    };

    const dateString = dayjst().format('YYYY-MM-DD');

    queryClient
      .fetchQuery(
        [newsType, _offset, selectedAuthorId || 'all', dateString],
        async () =>
          client.getContents(newsType, {
            offset: _offset,
            limit: LIMIT,
            fields: Fields[newsType],
            filters: getFilters(),
            orders:
              import.meta.env.NODE_ENV === 'production'
                ? '-publishedAt'
                : undefined,
          }),
      )
      .then(res => {
        if (!res || res.length < LIMIT) {
          setHasNextPage(prev => {
            const temp = { ...prev };
            temp[newsType] = false;
            return temp;
          });
        }
        setContents(prev => {
          const temp = { ...prev };
          temp[newsType] = [..._contents, ...res];
          return temp;
        });
        setOffset(prev => {
          const temp = { ...prev };
          temp[newsType] = prev[newsType] + LIMIT;
          return temp;
        });
      })
      .finally(() => setIsFetching(false));
  };

  useEffect(() => {
    setContents(initData([]));
    fetchContents(true);

    let breadcrumbs = [
      {
        name: getNewsTypeName(newsType),
        to: '',
      },
    ];
    const author = authors.find(a => a.id === selectedAuthorId);
    if (author && newsType === NewsType.Blog) {
      breadcrumbs = [
        {
          name: getNewsTypeName(newsType),
          to: `/${newsType}`,
        },
        {
          name: author.name,
          to: '',
        },
      ];
    }
    setBreadcrumbs(breadcrumbs);
  }, [newsType, selectedAuthorId]);

  useEffect(() => {
    if (selectedAuthorId) {
      queryParams.set(AUTHOR_PARAM_KEY, selectedAuthorId);
      navigate(`${location.pathname}?${queryParams.toString()}`, {
        replace: true,
      });
    }
  }, [selectedAuthorId]);

  useEffect(() => {
    if (newsType === NewsType.Blog) {
      // 著者一覧を取得
      queryClient
        .fetchQuery('blog_author', async () =>
          client.getContents('author', { fields: 'id,name', limit: 100 }),
        )
        .then(res => {
          setAuthors(res);
        });
    }
  }, [newsType]);

  const title = getNewsTypeName(newsType);

  const description = (type?: NewsType) => {
    switch (type) {
      case NewsType.News: {
        return 'DMM競輪のお知らせページ。サービスについての情報やメンテナンス告知など重要なニュースをご案内します。';
      }
      case NewsType.Campaign: {
        return 'DMM競輪のキャンペーン情報ページ。お得なキャンペーン情報をご案内します。';
      }
      case NewsType.Blog: {
        return 'DMM競輪のブログです。競輪情報番組・競輪選手ブログ・全国競輪場紹介・競輪入門講座などが充実。';
      }
      default: {
        return '';
      }
    }
  };

  return (
    <>
      <Helmet
        meta={{
          title,
          description: description(newsType),
          keywords: `${title},%s`,
        }}
      />
      <NewsTemplate
        contents={contents[newsType]}
        authors={authors}
        selectedAuthorId={selectedAuthorId}
        newsType={newsType}
        loading={isFetching}
        hasNextPage={newsType ? hasNextPage[newsType] : false}
        fetchContents={fetchContents}
        setSelectedAuthorId={setSelectedAuthorId}
      />
    </>
  );
};
export default News;
